import React, { Fragment, useEffect, useState } from "react"
import { toast } from "react-toastify";

const allocationTableStyle = {
  tableLayout: "fixed",
  width: "100%"
}

const maximumPreviewQuantity = quantitiesAvailableToCustomer => {
  if (!quantitiesAvailableToCustomer || quantitiesAvailableToCustomer.length === 0) {
    return 0
  }

  return Math.max(...quantitiesAvailableToCustomer)
}

const soldOut = optionsForSelect =>
  optionsForSelect.length === 0 ||
  !optionsForSelect.some(option => Number(option.props.value) > 0)

const lineItemName = props => {
  if (props.productType === "Bundle") {
    return props.productName
  }

  return `${props.vintageYear} ${props.wineName} ${props.size}`
}

const displaySequenceLabel = props => {
  if (!props.previewMode) {
    return null
  }

  if (
    props.displaySequence === null ||
    props.displaySequence === undefined ||
    props.displaySequence === ''
  ) {
    return null
  }

  return (
    <span
      style={{
        display: "inline-block",
        width: "3rem",
        fontWeight: "bold"
      }}
    >
      {props.displaySequence}.
    </span>
  )
}

const formatMoney = value => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const numericValue = Number(value.toString().replace(/[^0-9.-]+/g, ""))

  if (Number.isNaN(numericValue) || numericValue === 0) {
    return null
  }

  return `$${numericValue.toFixed(2)}`
}

const ProductInformationButton = props => {
  const [showModal, setShowModal] = useState(false)

  if (!props.productInformation || props.productInformation.trim() === '') {
    return null
  }

  const handleCloseModal = () => {
    setShowModal(false)
  }

  const ProductInformationModal = () => (
    <div className={`product-info-modal ${showModal ? 'd-block' : 'd-none'}`}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Product Information</h5>
            <button type="button" className="close" onClick={handleCloseModal}>
              <span>&times;</span>
            </button>
          </div>
          <div className="modal-body">
            <div dangerouslySetInnerHTML={{ __html: props.productInformation }} />
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <>
      <button
        type="button"
        className="btn btn-sm btn-primary"
        onClick={() => setShowModal(true)}
      >
        Product Information
      </button>

      {showModal && <ProductInformationModal />}
    </>
  )
}

const CellarDoorPrice = props => {
  const formattedPrice = formatMoney(props.price)

  if (!formattedPrice) {
    return null
  }

  return (
    <span style={{ color: '#b22222', textDecoration: 'line-through' }}>
      {formattedPrice}
    </span>
  )
}

// WDCE-333: the New Release Preview adds Seq, Product Code and Edit columns
// before the product name; every other page keeps its six columns.
const columnWidths = previewMode => (
  previewMode
    ? {
      seq: "5%",
      productCode: "10%",
      edit: "7%",
      name: "27%",
      productInformation: "14%",
      cellarDoorPrice: "9%",
      price: "9%",
      quantity: "8%",
      total: "11%"
    }
    : {
      name: "41%",
      productInformation: "17%",
      cellarDoorPrice: "10%",
      price: "12%",
      quantity: "10%",
      total: "10%"
    }
)

const ProductColumns = props => {
  const widths = columnWidths(props.previewMode)

  return (
    <>
      {props.previewMode && (
        <>
          <td style={{ width: widths.seq }}>
            {displaySequenceLabel(props)}
          </td>

          <td style={{ width: widths.productCode }}>
            {props.productCode}
          </td>

          <td className="text-center" style={{ width: widths.edit }}>
            {props.editPath && (
              <a
                className="btn btn-sm btn-primary text-white"
                href={props.editPath}
                target="_blank"
                rel="noopener noreferrer"
              >
                Edit
              </a>
            )}
          </td>
        </>
      )}

      <td style={{ width: widths.name }}>
        <span>{lineItemName(props)}</span>
      </td>

      <td className="text-center" style={{ width: widths.productInformation }}>
        <ProductInformationButton productInformation={props.productInformation} />
      </td>

      <td className="text-right" style={{ width: widths.cellarDoorPrice }}>
        <CellarDoorPrice price={props.cellarDoorPrice} />
      </td>

      <td className="text-right" style={{ width: widths.price }}>
        {props.price}
      </td>
    </>
  )
}

const OrderLineItem = props => {
  const id = props.id
  const price = Number(props.price.replace(/[^0-9.-]+/g, ""))
  const initialMaxQuantity = props.initialMaxQuantity
  const [currentSelect, setCurrentSelect] = useState(
    orderedCount(props.orderedAdditionalProducts, props.productId)
  )

  const [currentTotal, setCurrentTotal] = useState(price * currentSelect)

  const [loading, setLoading] = useState(false)
  const productsPath = props.productsPath
  const orderId = props.orderId
  const orderDetailId = props.orderDetailId

  let quantitiesAvailableToCustomer = props.quantitiesAvailableToCustomer

  let optionsForSelect = quantitiesAvailableToCustomer
    .filter(value => value <= initialMaxQuantity)
    .map(value => (
      <option key={value} value={value}>{value}</option>
    ));

  if (props.paid === false) {
    return (
      <tr>
        <ProductColumns {...props} />

        <td className="text-right" style={{ width: "10%" }}>
          {selectAdditionalWineQuantity(
            props.maxPerOrder,
            optionsForSelect,
            id,
            orderId,
            productsPath,
            price,
            currentSelect,
            setCurrentSelect,
            setCurrentTotal,
            props.handleBottlesCountChange,
            addToCart,
            orderDetailId,
            props.shippableItems,
            loading,
            setLoading
          )}
        </td>

        <td className="text-right" style={{ width: "10%" }}>
          ${currentTotal}.00
        </td>
      </tr>
    )
  } else {
    const widths = columnWidths(props.previewMode)

    return (
      <tr>
        <ProductColumns {...props} />

        <td className="text-right" style={{ width: widths.quantity }}>
          {props.previewMode
            ? maximumPreviewQuantity(quantitiesAvailableToCustomer)
            : currentSelect}
        </td>

        <td className="text-right" style={{ width: widths.total }}>
          ${currentTotal}.00
        </td>
      </tr>
    )
  }
}

const OrderTotal = props => {
  return (
    <tr className="order-total">
      <td
        className="cell-label cell-label--number text-right"
        colSpan="4"
      >
      </td>

      <td className="cell-label cell-label--number text-right">
        <strong>{props.name}</strong>
      </td>

      <td className="cell-data cell-data--number text-right">
        {(props.currency === true) ? '$' : ''}
        {props.value}
        {(props.currency === true) ? '.00' : ''}
      </td>
    </tr>
  )
}

const OrderTotalAdditional = props => {
  return (
    <tr className="order-total">
      <td
        className="cell-label cell-label--number text-right"
        colSpan={props.previewMode ? "7" : "4"}
      >
      </td>

      <td className="cell-label cell-label--number text-right">
        <strong>{props.name}</strong>
      </td>

      <td className="cell-data cell-data--number text-right">
        {(props.currency === true) ? '$' : ''}
        {props.value}
        {(props.currency === true) ? '.00' : ''}
      </td>
    </tr>
  )
}

const orderedCount = (orderDetails, product_id) => {
  if ((orderDetails == undefined) || (orderDetails.length == 0)) {
    return 0
  }

  var orderDetail = orderDetails.find(
    order_detail => order_detail.productId == product_id
  )

  return orderDetail == undefined ? 0 : orderDetail.quantity
}

const OrderAdditionalWineLineItem = props => {
  const id = props.id
  const price = Number(props.price.replace(/[^0-9.-]+/g, ""))
  const initialMaxQuantity = props.initialMaxQuantity

  const [currentSelect, setCurrentSelect] = useState(
    orderedCount(props.orderedAdditionalProducts, id)
  )

  const [currentTotal, setCurrentTotal] = useState(price * currentSelect)
  const [loading, setLoading] = useState(false)
  const productsPath = props.productsPath
  const orderId = props.orderId

  let quantitiesAvailableToCustomer = props.quantitiesAvailableToCustomer

  let optionsForSelect = quantitiesAvailableToCustomer
    .filter(value => value <= initialMaxQuantity)
    .map(value => (
      <option key={value} value={value}>{value}</option>
    ));

  if (props.paid === false) {
    return (
      <tr>
        <ProductColumns {...props} />

        <td className="text-right" style={{ width: "10%" }}>
          {selectAdditionalWineQuantity(
            props.maxPerOrder,
            optionsForSelect,
            id,
            orderId,
            productsPath,
            price,
            currentSelect,
            setCurrentSelect,
            setCurrentTotal,
            props.handleBottlesCountChange,
            addToCart,
            null,
            props.shippableItems,
            loading,
            setLoading
          )}
        </td>

        <td className="text-right" style={{ width: "10%" }}>
          ${currentTotal}.00
        </td>
      </tr>
    )
  } else {
    const widths = columnWidths(props.previewMode)

    return (
      <tr>
        <ProductColumns {...props} />

        <td className="text-right" style={{ width: widths.quantity }}>
          {props.previewMode
            ? maximumPreviewQuantity(quantitiesAvailableToCustomer)
            : currentSelect}
        </td>

        <td className="text-right" style={{ width: widths.total }}>
          ${currentTotal}.00
        </td>
      </tr>
    )
  }
}

const selectAdditionalWineQuantity = (
  maxPerOrder,
  optionsForSelect,
  id,
  orderId,
  productsPath,
  price,
  currentSelect,
  setCurrentSelect,
  setCurrentTotal,
  handleBottlesCountChange,
  addToCart,
  orderDetailId,
  shippableItems,
  loading,
  setLoading
) => {
  if (currentSelect == 0 && soldOut(optionsForSelect)) {
    return (
      <span style={{ color: '#b22222' }}>Sold Out</span>
    )
  } else {
    return (
      <select
        value={currentSelect}
        disabled={loading}
        className="additional-wine-quantity-select"
        onChange={
          e => {
            addToCart(
              id,
              e.target.value,
              orderId,
              productsPath,
              price,
              currentSelect,
              setCurrentSelect,
              setCurrentTotal,
              handleBottlesCountChange,
              orderDetailId,
              shippableItems,
              setLoading
            )
          }
        }
      >
        {optionsForSelect}
      </select>
    )
  }
}

const ShoppingCartErrorMessage = (props) => (
  <div>
    <h4>Order errors</h4>

    <ul>
      {Object.entries(props.errors).map(([key, value]) =>
        <li key={key}><span>{key}</span> {value}</li>
      )}
    </ul>
  </div>
)

const addToCart = (
  productId,
  quantity,
  orderId,
  productsPath,
  price,
  currentSelect,
  setCurrentSelect,
  setCurrentTotal,
  handleBottlesCountChange,
  orderDetailId,
  shippableItems,
  setLoading
) => {
  if (quantity.toString().length == 0) {
    return;
  }

  let body = new FormData();

  body.append("quantity", quantity);
  body.append("product_id", productId);
  body.append("order_id", orderId);
  if (orderDetailId) {
    body.append("order[order_details_attributes][id]", orderDetailId);
  }
  setLoading(true)

  return fetch(`${productsPath}.json`, {
    body: body,
    method: "PATCH",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": document
        .querySelector("meta[name='csrf-token']")
        .getAttribute("content"),
    },
  })
    .then(response => response.json())
    .then(response => {
      if (
        response.order != null &&
        Object.keys(response.order.errors).length === 0
      ) {
        const finalQuantity = Number(quantity)

        let differenceBottles =
          (finalQuantity - currentSelect) * (shippableItems || 1)
        let differenceMoney = (finalQuantity - currentSelect) * price

        setCurrentSelect(finalQuantity);
        setCurrentTotal(finalQuantity * price)

        handleBottlesCountChange(
          differenceBottles,
          differenceMoney
        )
      } else if (response.order != null) {
        const maxValue = Number(response.order.errors.left_in_stock)
        const finalQuantity = Number(response.order.errors.quantity || maxValue)
        const productType = response.order.errors.product_type
        const unit = productType === "Bundle" ? "packs" : "bottles"

        alert(`Only ${maxValue} ${unit} left in stock`)

        let differenceBottles =
          (finalQuantity - currentSelect) * (shippableItems || 1)
        let differenceMoney = (finalQuantity - currentSelect) * price

        setCurrentSelect(finalQuantity)
        setCurrentTotal(finalQuantity * price)

        handleBottlesCountChange(
          differenceBottles,
          differenceMoney
        )
      } else if (response.errors.deleted_order_alert != null) {
        alert(response.errors.deleted_order_alert)
        window.open(`/`, '_self');
      }

      return response;
    })
    .catch(error => {
      toast(
        <ShoppingCartErrorMessage
          errors={{ error: "Unexpected error occurred" }}
        />,
        { className: "shopping-cart-popup" }
      )
    })
    .finally(() => {
      setLoading(false)
    });
}

const sumElements = (arr) => {
  if (arr.length == 0) {
    return 0
  }

  const reducer = (accumulator, curr) => accumulator + curr;

  return (arr.reduce(reducer));
}

const calculateDeliveryFee = (
  totalBottles,
  allocationProductsPrice,
  additionalProductsPrice,
  selfPickUp,
  deliveryFeeOverride,
  initialDeliveryFee,
  deliveryFeeThreshold,
  defaultDeliveryFee
) => {
  if (deliveryFeeOverride == true) return initialDeliveryFee

  if (selfPickUp == 1 || selfPickUp == 2) {
    return 0
  }

  if ((additionalProductsPrice + allocationProductsPrice) >= deliveryFeeThreshold) {
    return 0
  }

  return Math.ceil(totalBottles / 12) * defaultDeliveryFee
}

const TableHeader = props => {
  const widths = columnWidths(props.previewMode)

  return (
    <thead>
      <tr>
        {props.previewMode && (
          <>
            <th style={{ width: widths.seq }}>
              Seq
            </th>

            <th style={{ width: widths.productCode }}>
              Product Code
            </th>

            <th
              className="text-center"
              style={{ width: widths.edit }}
            >
              Edit
            </th>
          </>
        )}

        <th style={{ width: widths.name }}>
          {props.productHeading}
        </th>

        <th
          className="text-center"
          style={{ width: widths.productInformation }}
        >
          Product Information
        </th>

        <th
          className="text-right"
          style={{ width: widths.cellarDoorPrice }}
        >
          Cellar<br />
          Door<br />
          Price
        </th>

        <th
          className="text-right"
          style={{ width: widths.price }}
        >
          Members<br />
          Release<br />
          Price
        </th>

        <th
          className="text-right"
          style={{ width: widths.quantity }}
        >
          Quantity
        </th>

        <th
          className="text-right"
          style={{ width: widths.total }}
        >
          Total Price
        </th>
      </tr>
    </thead>
  )
}

// TODO: refactor this madness
export default props => {
  const paid = props.paid
  let selfPickUp = props.selfPickUp
  const deliveryFeeOverride = props.deliveryFeeOverride || false

  const defaultDeliveryFee = Number(props.releaseYear.deliveryFeePerDozen)
  const deliveryFeeThreshold = Number(props.releaseYear.deliveryFeeThreshold)

  const initialDeliveryFee =
    parseFloat(props.deliveryFee.replace(/[^0-9.]/g, "")) || defaultDeliveryFee

  // WDCE-281: previewMode bypasses the quantity>0 check below. The New
  // Release Preview renders with paid=true but every line item has
  // quantity=0 (nothing has been ordered yet), so without this bypass
  // the quantity filter (added in WDCE-274 for the paid thank-you page)
  // silently excludes all New Release / Special Release wines from the
  // preview. Additional wines are unaffected since they're rendered from
  // a separate props.additionalProducts list, not this filter.
  const allocationProducts = props.orderDetails.filter(
    item =>
      !item.customerGroupsNames.includes('Allocation Additional Products') &&
      (props.paid === false || props.previewMode || Number(item.quantity) > 0)
  )

  const annualWinesProducts =
    allocationProducts.filter(value => value.releaseType == "annual")

  const specialReleaseWinesProducts =
    allocationProducts.filter(value => value.releaseType == "special_release")

  const bundleProducts =
    allocationProducts.filter(
      value => value.productType == "Bundle" && !value.releaseType
    )

  const [allocationBottlesCount, setAllocationBottlesCount] = useState(
    sumElements(allocationProducts.map(item => item.shippingBottleCount || 0))
  )

  const [currentTotalAllocationPrice, setCurrentTotalAllocationPrice] =
    useState(
      sumElements(
        allocationProducts.map(
          item => Number(item.total.replace(/[^0-9.-]+/g, ""))
        )
      )
    )

  const orderedProducts = props.orderDetails.filter(
    item => !item.customerGroupsNames.includes('Allocation Additional Products')
  )

  const orderedAdditionalProducts = props.orderDetails.filter(
    item => item.customerGroupsNames.includes('Allocation Additional Products')
  )

  const [currentAdditionalBottlesCount, setCurrentAdditionalBottlesCount] =
    useState(
      sumElements(orderedAdditionalProducts.map(item => item.shippingBottleCount || 0))
    )

  const [currentTotalAdditionalPrice, setCurrentTotalAdditionalPrice] =
    useState(
      sumElements(
        orderedAdditionalProducts.map(
          item => Number(item.total.replace(/[^0-9.-]+/g, ""))
        )
      )
    )

  const [currentDeliveryFee, setCurrentDeliveryFee] = useState(
    calculateDeliveryFee(
      (currentAdditionalBottlesCount + allocationBottlesCount),
      currentTotalAllocationPrice,
      currentTotalAdditionalPrice,
      selfPickUp,
      deliveryFeeOverride,
      initialDeliveryFee,
      deliveryFeeThreshold,
      defaultDeliveryFee
    )
  )

  const [grandTotal, setGrandTotal] = useState(
    currentTotalAllocationPrice +
    currentDeliveryFee +
    currentTotalAdditionalPrice
  )

  useEffect(() => props.setTotalAmount(grandTotal), [grandTotal]);

  useEffect(() => {
    let newFee = calculateDeliveryFee(
      (currentAdditionalBottlesCount + allocationBottlesCount),
      currentTotalAllocationPrice,
      currentTotalAdditionalPrice,
      selfPickUp,
      deliveryFeeOverride,
      initialDeliveryFee,
      deliveryFeeThreshold,
      defaultDeliveryFee
    )

    setCurrentDeliveryFee(newFee)

    setGrandTotal(
      currentTotalAllocationPrice +
      newFee +
      currentTotalAdditionalPrice
    );
  },
    [selfPickUp]
  );

  const handleAllocationBottlesCountChange = (
    differenceBottles,
    differenceMoney
  ) => {
    const newAllocationBottlesCount =
      allocationBottlesCount + differenceBottles

    const newTotalAllocationPrice =
      currentTotalAllocationPrice + differenceMoney

    const newDeliveryFee = calculateDeliveryFee(
      newAllocationBottlesCount + currentAdditionalBottlesCount,
      newTotalAllocationPrice,
      currentTotalAdditionalPrice,
      selfPickUp,
      deliveryFeeOverride,
      initialDeliveryFee,
      deliveryFeeThreshold,
      defaultDeliveryFee
    )

    setAllocationBottlesCount(newAllocationBottlesCount)
    setCurrentTotalAllocationPrice(newTotalAllocationPrice)
    setCurrentDeliveryFee(newDeliveryFee)

    setGrandTotal(
      newTotalAllocationPrice +
      currentTotalAdditionalPrice +
      newDeliveryFee
    )
  };

  const handleAdditionalBottlesCountChange = (
    differenceBottles,
    differenceMoney
  ) => {
    setCurrentAdditionalBottlesCount(
      currentAdditionalBottlesCount + differenceBottles
    )

    setCurrentTotalAdditionalPrice(
      currentTotalAdditionalPrice + differenceMoney
    )

    const newDeliveryFee = calculateDeliveryFee(
      (
        allocationBottlesCount +
        currentAdditionalBottlesCount +
        differenceBottles
      ),
      currentTotalAllocationPrice,
      (currentTotalAdditionalPrice + differenceMoney),
      selfPickUp,
      deliveryFeeOverride,
      initialDeliveryFee,
      deliveryFeeThreshold,
      defaultDeliveryFee
    )

    setCurrentDeliveryFee(newDeliveryFee)

    setGrandTotal(
      currentTotalAllocationPrice +
      currentTotalAdditionalPrice +
      differenceMoney +
      newDeliveryFee
    )
  };

  const checkOrderedIfOrderPaid = () => {
    if (props.previewMode) {
      return true
    }

    if (paid === false) {
      return true;
    } else {
      return orderedAdditionalProducts.filter(
        product => product.quantity !== 0
      ).length > 0;
    }
  };

  const needToShowAdditionalSection = () => {
    return (
      typeof props.additionalProducts !== 'undefined' &&
      props.additionalProducts.some(
        product =>
          product.quantitiesAvailableToCustomer &&
          product.quantitiesAvailableToCustomer.some(quantity => quantity > 0)
      ) &&
      checkOrderedIfOrderPaid()
    )
  }

  return (
    <div>
      {bundleProducts.length > 0 && (
        <>
          <table
            className="table table-striped table-hover allocation-table"
            style={allocationTableStyle}
          >
            <TableHeader productHeading="Mixed Packs" previewMode={props.previewMode} />

            <tbody>
              {bundleProducts.map(lineItem =>
                <OrderLineItem
                  key={lineItem.id}
                  {...lineItem}
                  orderDetailId={lineItem.id}
                  productId={lineItem.productId}
                  maxPerOrder={lineItem.maxQuantityPerOrder}
                  quantitiesAvailableToCustomer={lineItem.quantitiesAvailableToCustomer}
                  handleBottlesCountChange={handleAllocationBottlesCountChange}
                  productsPath={props.orderId}
                  orderId={props.orderId}
                  orderedAdditionalProducts={orderedProducts}
                  paid={props.paid}
                  previewMode={props.previewMode}
                />
              )}
            </tbody>
          </table>
        </>
      )}

      {annualWinesProducts.length > 0 && (
        <>
          <table
            className="table table-striped table-hover allocation-table"
            style={allocationTableStyle}
          >
            <TableHeader productHeading="New Release Wines" previewMode={props.previewMode} />

            <tbody>
              {annualWinesProducts.map(lineItem =>
                <OrderLineItem
                  key={lineItem.id}
                  {...lineItem}
                  orderDetailId={lineItem.id}
                  productId={lineItem.productId}
                  maxPerOrder={lineItem.maxQuantityPerOrder}
                  quantitiesAvailableToCustomer={lineItem.quantitiesAvailableToCustomer}
                  handleBottlesCountChange={handleAllocationBottlesCountChange}
                  productsPath={props.orderId}
                  orderId={props.orderId}
                  orderedAdditionalProducts={orderedProducts}
                  paid={props.paid}
                  previewMode={props.previewMode}
                />
              )}
            </tbody>
          </table>
        </>
      )}

      {specialReleaseWinesProducts.length > 0 && (
        <>
          <table
            className="table table-striped table-hover allocation-table"
            style={allocationTableStyle}
          >
            <TableHeader productHeading="Special Release Wines" previewMode={props.previewMode} />

            <tbody>
              {specialReleaseWinesProducts.map(lineItem =>
                <OrderLineItem
                  key={lineItem.id}
                  {...lineItem}
                  orderDetailId={lineItem.id}
                  productId={lineItem.productId}
                  maxPerOrder={lineItem.maxQuantityPerOrder}
                  quantitiesAvailableToCustomer={lineItem.quantitiesAvailableToCustomer}
                  handleBottlesCountChange={handleAllocationBottlesCountChange}
                  productsPath={props.orderId}
                  orderId={props.orderId}
                  orderedAdditionalProducts={orderedProducts}
                  paid={props.paid}
                  previewMode={props.previewMode}
                />
              )}
            </tbody>
          </table>
        </>
      )}
      {needToShowAdditionalSection() && (
        <>
          <h2 className="text-center">
            {props.paid === true ? '' : 'Select'} Optional Additional Wines
          </h2>

          <table
            className="table table-striped table-hover allocation-table additional-products-order"
            style={allocationTableStyle}
          >
            <TableHeader productHeading="Additional Allocation Wines" previewMode={props.previewMode} />

            <tbody>
              {props.additionalProducts.map(lineItem =>
                <OrderAdditionalWineLineItem
                  key={lineItem.id}
                  {...lineItem}
                  maxPerOrder={lineItem.maxQuantityPerOrder}
                  quantitiesAvailableToCustomer={lineItem.quantitiesAvailableToCustomer}
                  handleBottlesCountChange={handleAdditionalBottlesCountChange}
                  productsPath={props.productsPath}
                  orderId={props.orderId}
                  orderedAdditionalProducts={orderedAdditionalProducts}
                  paid={props.paid}
                  previewMode={props.previewMode}
                />
              )}

              <OrderTotalAdditional
                name="Total Additional Bottles"
                value={currentAdditionalBottlesCount}
                previewMode={props.previewMode}
              />

              <OrderTotalAdditional
                name="Total for Additional"
                currency={true}
                value={currentTotalAdditionalPrice}
                previewMode={props.previewMode}
              />
            </tbody>
          </table>
        </>
      )}
      <table
        className="table table-striped table-hover allocation-table"
        style={allocationTableStyle}
      >
        <tbody>
          <OrderTotal
            name="Delivery Fee"
            currency={true}
            value={currentDeliveryFee}
          />

          <OrderTotal
            name="Grand Total"
            currency={true}
            value={grandTotal}
          />
        </tbody>
      </table>
    </div>
  )
}
